import React from 'react'
import _cx from 'classnames/bind'
import Text from '../text'
import cls from './styles.scss'

const cx = _cx.bind(cls)

export interface StarRatingProps {
  /** Score to display (1-5). Null renders a fallback dash "—" */
  score: number | null
  /** Maximum number of stars (default: 5) */
  maxStars?: number
  /** Size variant matching the design system size convention */
  size?: 'small' | 'medium' | 'large'
  /** Automation target element ID */
  elmId?: string
}

const STAR_SIZES: Record<string, number> = {
  small: 12,
  medium: 16,
  large: 20,
}

const FILLED_COLOR = '#F59E0B'
const EMPTY_COLOR = '#D1D5DB'

const StarIcon: React.FC<{ filled: boolean; size: number }> = ({
  filled,
  size,
}) => (
  <svg
    className={cx('star')}
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill={filled ? FILLED_COLOR : EMPTY_COLOR}
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
  </svg>
)

const StarRating: React.FC<StarRatingProps> = ({
  score,
  maxStars = 5,
  size = 'medium',
  elmId = 'star-rating',
}) => {
  const starSize = STAR_SIZES[size]

  if (score === null || score === undefined) {
    return (
      <span className={cx('container', size)} data-elm-id={`${elmId}-null`}>
        <Text>—</Text>
      </span>
    )
  }

  const clampedScore = Math.min(Math.max(Math.round(score), 1), maxStars)

  return (
    <span
      className={cx('container', size)}
      role='img'
      aria-label={`${clampedScore} out of ${maxStars}`}
      data-elm-id={elmId}
    >
      {Array.from({ length: maxStars }, (_, i) => (
        <StarIcon key={i} filled={i < clampedScore} size={starSize} />
      ))}
    </span>
  )
}

export default StarRating
